import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-message-3-line',
    title: 'Primeiro Contato',
    desc: 'Entre em contato pelo WhatsApp. Conte brevemente sua situação — sem pressa, sem julgamentos.',
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'Análise do Caso',
    desc: 'Leide escuta com atenção, analisa os documentos e avalia as possibilidades jurídicas.',
  },
  {
    number: '03',
    icon: 'ri-lightbulb-line',
    title: 'Orientação Jurídica',
    desc: 'Você recebe um plano claro: caminhos, prazos e o que esperar em cada etapa.',
  },
  {
    number: '04',
    icon: 'ri-shield-check-line',
    title: 'Acompanhamento Completo',
    desc: 'Leide conduz todo o processo com comunicação transparente até a resolução.',
  },
];

export default function HowItWorks() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-funciona" ref={ref} className="py-24 bg-parchment">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-10 bg-chestnut"></div>
            <span className="text-chestnut text-xs tracking-[0.2em] uppercase font-sans font-medium">Processo</span>
            <div className="h-px w-10 bg-chestnut"></div>
          </div>
          <h2 className="font-serif text-ink-black text-3xl md:text-4xl font-semibold mb-4">
            Metodologia de Trabalho
          </h2>
          <p className="text-body-gray font-sans max-w-xl mx-auto text-base">
            Um processo simples e transparente — para que você se sinta seguro em cada passo.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          <div className="hidden lg:block absolute top-12 left-[12%] right-[12%] h-px bg-chestnut/20 z-0"></div>

          {steps.map((step, idx) => (
            <div
              key={step.number}
              className={`relative z-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${idx * 120}ms` }}
            >
              <div className="bg-white rounded-xl p-7 text-center h-full flex flex-col items-center">
                <div className="relative mb-5">
                  <div className="w-14 h-14 flex items-center justify-center bg-ink-black rounded-full">
                    <i className={`${step.icon} text-burnt-peach text-xl`}></i>
                  </div>
                  <span className="absolute -top-1 -right-1 bg-chestnut text-white text-[10px] font-sans font-semibold w-5 h-5 flex items-center justify-center rounded-full">
                    {step.number}
                  </span>
                </div>
                <h3 className="font-serif text-ink-black text-lg font-semibold mb-3">{step.title}</h3>
                <p className="text-body-gray font-sans text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-12 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <a
            href="https://wa.me/5547984929295?text=Ol%C3%A1%2C%20quero%20dar%20o%20primeiro%20passo."
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex md:inline-flex items-center justify-center gap-3 bg-burnt-peach hover:bg-burnt-peach-light text-white font-sans font-medium px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap mx-5 md:mx-0"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Começar Agora
          </a>
        </div>
      </div>
    </section>
  );
}
